// src/components/RevibeNavbar.jsx
import React, { useState, useEffect, useRef } from "react";
import { Search, Home, Radio, Calendar, Menu, X, ChevronDown, User, LogOut, Bell } from "lucide-react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import logo from "../assets/Revibelogo.png";

const RevibeNavbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [showMobileTopBar, setShowMobileTopBar] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const dropdownRef = useRef(null);
  const hoverTimeoutRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const navItems = [
    { name: 'Home', icon: Home, path: '/dashboard' },
    { name: 'Live', icon: Radio, path: '/live' },
    { name: 'Search', icon: Search, path: '/search' },
    { name: 'Plan', icon: Calendar, path: '/plan' },
  ];

  const hideTopNavbarPaths = ["/live", "/search", "/plan", "/workout/","/checkout","/refer","/aboutus","/contactus","/terms","/profile" ];

  const shouldHideTopNavbar = hideTopNavbarPaths.some(path =>
    location.pathname.startsWith(path)
  );

  // Handle scroll to hide/show mobile top bar
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Hide after scrolling 600px
      if (currentScrollY > 780) {
        setShowMobileTopBar(false);
      } else {
        setShowMobileTopBar(true);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const loadUser = () => {
      const storedUser = localStorage.getItem("userData");
      setUser(storedUser ? JSON.parse(storedUser) : null);
    };

    loadUser();
    window.addEventListener("userUpdated", loadUser);
    return () => window.removeEventListener("userUpdated", loadUser);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("userData");
    localStorage.removeItem("token");
    localStorage.removeItem("authToken");
          localStorage.removeItem('userSubscription')
    localStorage.removeItem("userId");
    localStorage.removeItem("isProfileCreated");
    setUser(null);
    window.dispatchEvent(new Event("userUpdated"));
    navigate("/");
  };

  const getInitials = (firstName, lastName) => {
    return `${(firstName?.[0] || "").toUpperCase()}${(lastName?.[0] || "").toUpperCase()}`;
  };

  const handleMyProfile = () => {
    setIsProfileDropdownOpen(false);
    setIsMobileMenuOpen(false);
    navigate("/profile");
  };

  const handleMouseEnter = () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    setIsProfileDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setIsProfileDropdownOpen(false);
    }, 200);
  };

  const isActivePath = (path) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  return (
    <>
      {/* ─────────────────────────────────────────────────────────────
          MOBILE TOP BAR (visible only on mobile, hides after 300px scroll)
      ───────────────────────────────────────────────────────────── */}
      {!shouldHideTopNavbar && (
        <nav 
          className={`md:hidden bg-transparent sticky top-0 z-50 px-4 pt-3 pb-2 transition-all duration-300 ease-in-out ${
            showMobileTopBar ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'
          }`}
        >
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/">
              <img src={logo} alt="Revibe logo" className="h-9" />
            </Link>

            {/* Right side: greeting + profile icon + bell */}
            <div className="flex items-center gap-2">
              {user ? (
                <>
                  {/* Greeting pill */}
                  <button
                    onClick={() => navigate("/profile")}
                    className="flex items-center gap-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-full px-4 py-1.5"
                  >
                    <span className="text-[#c8f000] font-semibold text-sm">
                      Hii {user.firstName}
                    </span>
                    <div className="w-7 h-7 rounded-full bg-white/30 flex items-center justify-center">
                      <User className="w-4 h-4 text-white" />
                    </div>
                  </button>

                  {/* Bell */}
                  {/* <button className="relative w-9 h-9 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 flex items-center justify-center">
                    <Bell className="w-4 h-4 text-white" />
                    <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
                  </button> */}
                </>
              ) : (
                <Link
                  to="/login"
                  className="bg-[#c8f000] text-black font-semibold px-4 py-1.5 rounded-full text-sm"
                >
                  Get Started
                </Link>
              )}
            </div>
          </div>
        </nav>
      )}

      {/* ─────────────────────────────────────────────────────────────
          DESKTOP / TABLET TOP NAV (hidden on mobile)
      ───────────────────────────────────────────────────────────── */}
      <nav className="hidden md:block bg-white backdrop-blur-md border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 md:h-20">

            {/* Logo */}
            <Link to="/">
              <img src={logo} alt="Revibe logo" className="h-12" />
            </Link>

            {/* Desktop Navigation Links */}
            <div className="flex items-center justify-center space-x-1 lg:space-x-2">
              {navItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all duration-200 font-medium text-sm lg:text-base ${
                    isActivePath(item.path)
                      ? "text-purple-700 bg-purple-50"
                      : "text-gray-700 hover:text-gray-900 hover:bg-gray-100"
                  }`}
                >
                  <item.icon className="w-4 h-4" />
                  <span>{item.name}</span>
                </Link>
              ))}
            </div>

            {/* Desktop Right - User Profile or CTA */}
            <div className="flex items-center gap-3">
              {user ? (
                <div
                  className="relative"
                  ref={dropdownRef}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  <button className="flex items-center gap-2 bg-white px-3 py-2 rounded-xl border border-slate-200 shadow-sm hover:bg-slate-50 transition-colors cursor-pointer">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-600 to-indigo-600 flex items-center justify-center text-white text-xs font-medium flex-shrink-0">
                      {getInitials(user.firstName, user.lastName)}
                    </div>
                    <span className="text-sm font-medium text-slate-800">
                      {user.firstName} {user.lastName}
                    </span>
                    <ChevronDown
                      size={16}
                      className={`text-slate-500 transition-transform duration-200 ${
                        isProfileDropdownOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  {isProfileDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-slate-200 py-2 z-50 origin-top-right">
                      <div className="px-4 py-3 border-b border-slate-100">
                        <p className="text-xs text-slate-500 mb-1">Email</p>
                        <p className="text-sm font-medium text-slate-800 truncate">
                          {user.email}
                        </p>
                      </div>
                      <button
                        onClick={handleMyProfile}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                      >
                        <User size={16} className="text-slate-500" />
                        My Profile
                      </button>
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors border-t border-slate-100 mt-1"
                      >
                        <LogOut size={16} />
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  to="/login"
                  className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-5 py-2 rounded-full text-sm font-semibold shadow-md hover:shadow-lg transition-all duration-200 hover:scale-105"
                >
                  Get Started
                </Link>
              )}
            </div>
          </div>
        </div>
      </nav>
{/* ─────────────────────────────────────────────────────────────
    MOBILE BOTTOM TAB BAR (visible only on mobile)
───────────────────────────────────────────────────────────── */}
<div className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-gray-100 pt-3">
  
  <div className="px-3 pb-3">
    <div className="bg-white rounded-[30px] shadow-2xl shadow-black/20 flex items-center justify-around px-2 py-2">
      {navItems.map((item) => {
        const active = isActivePath(item.path);
        const isLive = item.name === 'Live';

        return (
          <Link
            key={item.name}
            to={item.path}
            className="flex flex-col items-center justify-center min-w-[60px]"
          >
            {isLive ? (
              /* LIVE button — special pill style */
              <div
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full transition-all ${
                  active ? "bg-red-600" : "bg-red-500"
                }`}
              >
                <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                <span className="text-white font-bold text-sm tracking-wide">LIVE</span>
              </div>
            ) : (
              <>
                <div
                  className={`p-2 rounded-full transition-all duration-200 ${
                    active ? "bg-[#c8f000]" : "bg-transparent"
                  }`}
                >
                  <item.icon
                    className={`w-5 h-5 transition-colors ${
                      active ? "text-black" : "text-gray-500"
                    }`}
                  />
                </div>
                <span
                  className={`text-[10px] mt-0.5 font-medium transition-colors ${
                    active ? "text-black" : "text-gray-500"
                  }`}
                >
                  {item.name}
                </span>
              </>
            )}
          </Link>
        );
      })}
    </div>
  </div>

</div>
      
    </>
  );
};

export default RevibeNavbar;